import React, { useEffect, useRef, useState } from 'react';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import Button from '@material-ui/core/Button';
import Checkbox from '@material-ui/core/Checkbox';
import CircularProgress from '@material-ui/core/CircularProgress';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import MenuItem from '@material-ui/core/MenuItem';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { Pagination } from '@material-ui/lab';
import { SkillsAndTransformation } from 'Services/SkillsAndTransformation';
import { notify } from 'utils';
import {
  DEFAULT_LEARNING_HUB_PAGE_SIZE,
  emptyHubPagination,
  HUB_TAB_LEARNING_HUB,
  LEARNING_HUB_PAGE_SIZES,
  normalizePageSize,
  resolveHubPagination,
} from './helpers';
import {
  APPROVED_MESSAGE,
  PENDING_MESSAGE,
  buildUpgradePayload,
  classifyUpgradeResponse,
  courseMeta,
  courseName,
  currentPlanFields,
  selectedCountLabel,
  upgradeSubmitLabel,
} from './upgradePackage';

const skillsAndTransformation = new SkillsAndTransformation();
const SEARCH_DELAY_MS = 300;

const useStyles = makeStyles((theme) => ({
  title: {
    fontWeight: 700,
    fontSize: '1.0625rem',
    color: '#263548',
  },
  intro: {
    color: '#647184',
    fontSize: '0.875rem',
    lineHeight: 1.5,
    marginBottom: theme.spacing(2),
  },
  plans: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 12,
    [theme.breakpoints.down('xs')]: {
      gridTemplateColumns: '1fr',
    },
  },
  planCard: {
    border: '1px solid #e7ebf0',
    borderRadius: 10,
    padding: theme.spacing(1.5),
    minWidth: 0,
  },
  optionCard: {
    borderColor: '#3d7ddd',
    backgroundColor: '#f5f8fc',
  },
  cardLabel: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    color: '#647184',
  },
  fieldList: {
    margin: theme.spacing(1, 0, 0),
  },
  field: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: 12,
    padding: '4px 0',
    fontSize: 13,
  },
  fieldLabel: {
    color: '#647184',
  },
  fieldValue: {
    color: '#263548',
    fontWeight: 600,
    textAlign: 'right',
  },
  optionNote: {
    color: '#4D5766',
    fontSize: 13,
    lineHeight: 1.45,
    marginTop: theme.spacing(0.5),
  },
  sectionTitle: {
    marginTop: theme.spacing(2.5),
    fontSize: 14,
    fontWeight: 700,
    color: '#263548',
  },
  toolbar: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 12,
    alignItems: 'center',
    marginTop: theme.spacing(1.5),
  },
  search: {
    flex: '1 1 220px',
  },
  pageSize: {
    width: 140,
  },
  count: {
    fontSize: 13,
    fontWeight: 600,
    color: '#263548',
    marginTop: theme.spacing(1.5),
  },
  selectedList: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
    marginTop: theme.spacing(1),
    padding: 0,
    listStyle: 'none',
  },
  selectedItem: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    border: '1px solid #d5deea',
    borderRadius: 16,
    padding: '2px 4px 2px 10px',
    fontSize: 13,
    color: '#263548',
  },
  courseList: {
    listStyle: 'none',
    margin: theme.spacing(1.5, 0, 0),
    padding: 0,
    border: '1px solid #e7ebf0',
    borderRadius: 10,
    maxHeight: 320,
    overflow: 'auto',
  },
  courseRow: {
    borderBottom: '1px solid #e7ebf0',
    '&:last-child': {
      borderBottom: 'none',
    },
  },
  courseLabel: {
    display: 'flex',
    alignItems: 'flex-start',
    margin: 0,
    padding: theme.spacing(0.5, 1),
    width: '100%',
  },
  courseText: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
    paddingTop: 8,
  },
  courseName: {
    fontSize: 14,
    fontWeight: 600,
    color: '#263548',
  },
  courseMeta: {
    fontSize: 12,
    color: '#647184',
  },
  statusBox: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(1.5),
    borderRadius: 10,
    backgroundColor: '#f4f7fb',
    color: '#263548',
    fontSize: 14,
  },
  error: {
    color: '#9A3412',
    fontSize: 13,
    marginTop: theme.spacing(1),
  },
  center: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(3),
  },
  empty: {
    padding: theme.spacing(2),
    color: '#647184',
    fontSize: 14,
  },
  pager: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: theme.spacing(1.5),
  },
  comment: {
    marginTop: theme.spacing(2),
  },
  actions: {
    padding: theme.spacing(1.5, 3, 2),
    flexWrap: 'wrap',
    gap: 8,
  },
}));

const UpgradePackageDialog = ({ open, onClose, access, onAlreadyApproved }) => {
  const classes = useStyles();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('xs'));
  const [requestState, setRequestState] = useState('');
  const [statusMessage, setStatusMessage] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(0);
  const [perpage, setPerpage] = useState(DEFAULT_LEARNING_HUB_PAGE_SIZE);
  const [courses, setCourses] = useState([]);
  const [pagination, setPagination] = useState(() => emptyHubPagination());
  const [coursesLoading, setCoursesLoading] = useState(false);
  const [coursesError, setCoursesError] = useState('');
  const [reloadKey, setReloadKey] = useState(0);
  const [selected, setSelected] = useState({});
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const appliedSearch = useRef('');

  useEffect(() => {
    const timer = window.setTimeout(() => {
      const next = searchInput.trim();
      if (appliedSearch.current === next) {
        return;
      }
      appliedSearch.current = next;
      setDebouncedSearch(next);
      setPage(0);
    }, SEARCH_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    if (!open || requestState) {
      return undefined;
    }
    let cancelled = false;
    const loadCourses = async () => {
      setCoursesLoading(true);
      setCoursesError('');
      const response = await skillsAndTransformation.getLearningHub({
        tab: HUB_TAB_LEARNING_HUB,
        pageno: page,
        perpage,
        ...(debouncedSearch ? { search: debouncedSearch } : {}),
      });
      if (cancelled) {
        return;
      }
      const pageCourses = Array.isArray(response?.courses) ? response.courses : null;
      if (!pageCourses || response?.status === false) {
        setCourses([]);
        setPagination(emptyHubPagination(perpage));
        setCoursesError(response?.message || 'Unable to load Learning Hub courses');
      } else {
        setCourses(pageCourses);
        setPagination(resolveHubPagination(response.pagination, {
          pageno: page,
          perpage,
          courses: pageCourses,
        }));
        setCoursesError('');
      }
      setCoursesLoading(false);
    };
    loadCourses();
    return () => {
      cancelled = true;
    };
  }, [open, requestState, page, perpage, debouncedSearch, reloadKey]);

  const selectedCourses = Object.values(selected);
  const selectedCount = selectedCourses.length;
  const showForm = !requestState;
  const planFields = currentPlanFields(access);

  const toggleCourse = (course) => {
    const uuid = course?.uuid;
    if (!uuid) {
      return;
    }
    setSelected((current) => {
      const next = { ...current };
      if (next[uuid]) {
        delete next[uuid];
      } else {
        next[uuid] = {
          uuid,
          name: courseName(course),
          category: course?.category,
          provider: course?.provider || '',
        };
      }
      return next;
    });
  };

  const handleSubmit = async () => {
    if (submitting || requestState) {
      return;
    }
    setSubmitting(true);
    setSubmitError('');
    try {
      const response = await skillsAndTransformation.submitLearningHubUpgrade(
        buildUpgradePayload(selectedCourses.map((course) => course.uuid), comment)
      );
      const outcome = classifyUpgradeResponse(response);
      if (outcome === 'already_pending') {
        setRequestState('pending');
        setStatusMessage(PENDING_MESSAGE);
        notify('info', PENDING_MESSAGE);
        return;
      }
      if (outcome === 'pending') {
        setRequestState('pending');
        setStatusMessage('Your Learning Hub request is pending approval. Learning Hub is not active yet.');
        notify('success', response?.message || 'Learning Hub request submitted. It stays pending until it is approved.');
        return;
      }
      if (outcome === 'approved') {
        setRequestState('approved');
        notify('info', APPROVED_MESSAGE);
        if (onAlreadyApproved) {
          onAlreadyApproved();
        }
        return;
      }
      setSubmitError(response?.message || 'Unable to submit the Learning Hub request');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={submitting ? undefined : onClose}
      maxWidth="md"
      fullWidth
      fullScreen={fullScreen}
      aria-labelledby="upgrade-package-title"
    >
      <DialogTitle id="upgrade-package-title" disableTypography>
        <Typography className={classes.title} component="h2">Upgrade package</Typography>
      </DialogTitle>
      <DialogContent>
        <Typography className={classes.intro}>
          Learning Hub stays inactive until a Customer Admin or Project Admin approves this request. Selected courses are stored on the request and are not enrolled automatically.
        </Typography>
        <div className={classes.plans}>
          <section className={classes.planCard} aria-label="Current plan">
            <Typography className={classes.cardLabel}>Current plan</Typography>
            <dl className={classes.fieldList}>
              {planFields.map((field) => (
                <div key={field.label} className={classes.field}>
                  <dt className={classes.fieldLabel}>{field.label}</dt>
                  <dd className={classes.fieldValue}>{field.value}</dd>
                </div>
              ))}
            </dl>
          </section>
          <section className={`${classes.planCard} ${classes.optionCard}`} aria-label="Upgrade option">
            <Typography className={classes.cardLabel}>Upgrade option</Typography>
            <RadioGroup aria-label="Upgrade option" name="requested_plan" value="learning_hub">
              <FormControlLabel
                value="learning_hub"
                control={<Radio color="primary" inputProps={{ 'aria-label': 'Learning Hub upgrade option' }} />}
                label="Learning Hub"
              />
            </RadioGroup>
            <Typography className={classes.optionNote}>
              {requestState === 'approved'
                ? APPROVED_MESSAGE
                : 'This is the package you can request. It is not your active plan until the request is approved.'}
            </Typography>
          </section>
        </div>

        {requestState === 'pending' ? (
          <div className={classes.statusBox} role="status">{statusMessage || PENDING_MESSAGE}</div>
        ) : null}
        {requestState === 'approved' ? (
          <div className={classes.statusBox} role="status">{APPROVED_MESSAGE}</div>
        ) : null}

        {showForm ? (
          <>
            <Typography className={classes.sectionTitle} component="h3">Courses</Typography>
            <Typography className={classes.optionNote}>
              Course selection is optional. You can choose more than one.
            </Typography>
            <div className={classes.toolbar}>
              <TextField
                className={classes.search}
                variant="outlined"
                size="small"
                label="Search courses"
                value={searchInput}
                onChange={(event) => {
                  const nextValue = event.target.value;
                  setSearchInput(nextValue);
                }}
                inputProps={{ 'aria-label': 'Search courses by name' }}
              />
              <TextField
                className={classes.pageSize}
                select
                variant="outlined"
                size="small"
                label="Courses per page"
                value={perpage}
                onChange={(event) => {
                  setPerpage(normalizePageSize(event.target.value));
                  setPage(0);
                }}
              >
                {LEARNING_HUB_PAGE_SIZES.map((size) => (
                  <MenuItem key={size} value={size}>{size}</MenuItem>
                ))}
              </TextField>
            </div>
            <div className={classes.count} aria-live="polite">{selectedCountLabel(selectedCount)}</div>
            {selectedCount ? (
              <ul className={classes.selectedList} aria-label="Selected courses">
                {selectedCourses.map((course) => (
                  <li key={course.uuid} className={classes.selectedItem}>
                    <span>{course.name}</span>
                    <Button
                      size="small"
                      aria-label={`Remove ${course.name}`}
                      onClick={() => toggleCourse(course)}
                    >
                      Remove
                    </Button>
                  </li>
                ))}
              </ul>
            ) : null}
            {coursesLoading ? (
              <div className={classes.center} role="status" aria-label="Loading courses">
                <CircularProgress size={28} />
              </div>
            ) : coursesError ? (
              <div>
                <Typography className={classes.error} role="alert">{coursesError}</Typography>
                <Button color="primary" onClick={() => setReloadKey((current) => current + 1)}>
                  Try again
                </Button>
              </div>
            ) : courses.length ? (
              <ul className={classes.courseList} aria-label="Learning Hub courses">
                {courses.map((course) => {
                  const name = courseName(course);
                  const meta = courseMeta(course);
                  return (
                    <li key={course.uuid || name} className={classes.courseRow}>
                      <FormControlLabel
                        className={classes.courseLabel}
                        control={(
                          <Checkbox
                            color="primary"
                            checked={!!(course.uuid && selected[course.uuid])}
                            onChange={() => toggleCourse(course)}
                            inputProps={{ 'aria-label': `Select ${name}` }}
                          />
                        )}
                        label={(
                          <span className={classes.courseText}>
                            <span className={classes.courseName}>{name}</span>
                            {meta ? <span className={classes.courseMeta}>{meta}</span> : null}
                          </span>
                        )}
                      />
                    </li>
                  );
                })}
              </ul>
            ) : (
              <Typography className={classes.empty}>No Learning Hub courses found.</Typography>
            )}
            {!coursesLoading && !coursesError && pagination.total_pages > 1 ? (
              <div className={classes.pager}>
                <Pagination
                  page={pagination.pageno + 1}
                  count={pagination.total_pages}
                  onChange={(_event, value) => setPage(value - 1)}
                  color="primary"
                  shape="rounded"
                  variant="outlined"
                  aria-label="Course pages"
                />
              </div>
            ) : null}
            <TextField
              className={classes.comment}
              variant="outlined"
              size="small"
              fullWidth
              multiline
              minRows={3}
              label="Comment"
              placeholder="Optional reason"
              value={comment}
              onChange={(event) => {
                const nextValue = event.target.value;
                setComment(nextValue);
              }}
              inputProps={{ 'aria-label': 'Optional reason' }}
            />
            {submitError ? (
              <Typography className={classes.error} role="alert">{submitError}</Typography>
            ) : null}
          </>
        ) : null}
      </DialogContent>
      <DialogActions className={classes.actions}>
        <Button onClick={onClose} disabled={submitting}>Close</Button>
        {showForm ? (
          <Button
            color="primary"
            variant="contained"
            disableElevation
            onClick={handleSubmit}
            disabled={submitting}
          >
            {submitting ? <CircularProgress size={16} color="inherit" /> : upgradeSubmitLabel(selectedCount)}
          </Button>
        ) : null}
      </DialogActions>
    </Dialog>
  );
};

export default UpgradePackageDialog;
